import {
  CSSProperties,
  createContext,
  Dispatch,
  MouseEvent,
  useRef,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import "./App.css"

import Board from "./components/Board"
import Keyboard from "./components/Keyboard"
import GameOver from "./components/GameOver"
import SettingsModal from "./components/SettingsModal"
import {
  createEmptyBoard,
  createEmptyBoardStatus,
  DEFAULT_SETTINGS,
  evaluateGuess,
  GameSettings,
  isWinningStatus,
  LetterStatus,
  MAX_ATTEMPTS,
  mergeLetterStatus,
} from "./helpers"
import {
  filterWordsByLength,
  loadGreekWordBanks,
  pickDailyWeightedWord,
  WeightedGreekWord,
} from "./lib/dailyWord"
import { foldGreekForCompare, toNFC } from "./lib/greekText"

const SETTINGS_STORAGE_KEY = "ancient-greek-wordle-settings"
const LETTER_REVEAL_DURATION_MS = 1260
const LETTER_REVEAL_STAGGER_MS = 270
const CONFETTI_HIDE_DELAY_MS = 3600

function getBrowserPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

function readStoredSettings(): GameSettings {
  const fallback = {
    ...DEFAULT_SETTINGS,
    darkMode: getBrowserPrefersDark(),
  }

  try {
    const rawSettings = window.localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (!rawSettings) {
      return fallback
    }

    const parsedSettings = JSON.parse(rawSettings) as Partial<GameSettings>
    const wordLength = parsedSettings.wordLength
    const validLength = wordLength === 4 || wordLength === 5 || wordLength === 6

    return {
      wordLength: validLength ? wordLength : fallback.wordLength,
      diacriticsStrictMode:
        typeof parsedSettings.diacriticsStrictMode === "boolean"
          ? parsedSettings.diacriticsStrictMode
          : fallback.diacriticsStrictMode,
      darkMode:
        typeof parsedSettings.darkMode === "boolean"
          ? parsedSettings.darkMode
          : fallback.darkMode,
    }
  } catch {
    return fallback
  }
}

type AttemptCursor = {
  attempt: number
  letterPos: number
}

type ShakeState = {
  row: number
  token: number
}

type ConfettiPiece = {
  left: number
  drift: number
  fallDelayMs: number
  fallDurationMs: number
  sizePx: number
  spinDeg: number
  color: string
}

function getRevealDurationMs(wordLength: number): number {
  return LETTER_REVEAL_DURATION_MS + (wordLength - 1) * LETTER_REVEAL_STAGGER_MS
}

function createConfettiPieces(count: number): ConfettiPiece[] {
  return Array.from({ length: count }, (_, index) => {
    const hue = (index * 31 + Math.random() * 45) % 360
    return {
      left: Math.random() * 100,
      drift: (Math.random() - 0.5) * 220,
      fallDelayMs: Math.random() * 260,
      fallDurationMs: 2000 + Math.random() * 1250,
      sizePx: 5 + Math.random() * 7,
      spinDeg: 420 + Math.random() * 680,
      color: `hsl(${hue}, 88%, 56%)`,
    }
  })
}

export interface IWordleGameContext {
  board: string[][]
  setBoard: Dispatch<SetStateAction<string[][]>>
  boardStatus: LetterStatus[][]
  setBoardStatus: Dispatch<SetStateAction<LetterStatus[][]>>
  currAttempt: AttemptCursor
  setCurrAttempt: Dispatch<SetStateAction<AttemptCursor>>
  onDelete: () => void
  onEnter: () => void
  onSelectLetter: (key: string) => void
  onReset: () => void
  correctWord: string
  letterStatus: Map<string, LetterStatus>
  gameOver: { gameOver: boolean; guessedWord: boolean }
  settings: GameSettings
  invalidMessage: string
  shakeState: ShakeState
}

export const AppContext = createContext<IWordleGameContext>(
  {} as IWordleGameContext
)

function App() {
  const [settings, setSettings] = useState<GameSettings>(() => readStoredSettings())
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isCreditsOpen, setIsCreditsOpen] = useState(false)
  const [isLoadingWords, setIsLoadingWords] = useState(true)
  const [wordLoadError, setWordLoadError] = useState("")
  const [answerWords, setAnswerWords] = useState<string[]>([])
  const [allowedWords, setAllowedWords] = useState<string[]>([])
  const [answerWeights, setAnswerWeights] = useState<WeightedGreekWord[]>([])
  const [correctWord, setCorrectWord] = useState("")

  const [board, setBoard] = useState(() => createEmptyBoard(settings.wordLength))
  const [boardStatus, setBoardStatus] = useState(() =>
    createEmptyBoardStatus(settings.wordLength)
  )
  const [currAttempt, setCurrAttempt] = useState<AttemptCursor>({
    attempt: 0,
    letterPos: 0,
  })
  const [letterStatus, setLetterStatus] = useState<Map<string, LetterStatus>>(
    new Map()
  )
  const [gameOver, setGameOver] = useState({
    gameOver: false,
    guessedWord: false,
  })
  const [invalidMessage, setInvalidMessage] = useState("")
  const [shakeState, setShakeState] = useState<ShakeState>({ row: -1, token: 0 })
  const [isResolvingAttempt, setIsResolvingAttempt] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [confettiPieces, setConfettiPieces] = useState<ConfettiPiece[]>([])
  const resolveTimerRef = useRef<number | null>(null)
  const confettiTimerRef = useRef<number | null>(null)

  const clearResolveTimer = () => {
    if (resolveTimerRef.current !== null) {
      window.clearTimeout(resolveTimerRef.current)
      resolveTimerRef.current = null
    }
  }

  const clearConfettiTimer = () => {
    if (confettiTimerRef.current !== null) {
      window.clearTimeout(confettiTimerRef.current)
      confettiTimerRef.current = null
    }
  }

  const triggerConfetti = () => {
    setConfettiPieces(createConfettiPieces(128))
    setShowConfetti(true)
    clearConfettiTimer()
    confettiTimerRef.current = window.setTimeout(() => {
      setShowConfetti(false)
    }, CONFETTI_HIDE_DELAY_MS)
  }

  useEffect(() => {
    let isCancelled = false

    async function loadWords() {
      try {
        const wordBanks = await loadGreekWordBanks()
        if (isCancelled) {
          return
        }

        setAnswerWords(wordBanks.answers)
        setAllowedWords(wordBanks.allowed)
        setAnswerWeights(wordBanks.answerWeights)
        setWordLoadError("")
      } catch {
        if (!isCancelled) {
          setWordLoadError(
            "Could not load Greek word lists. Check public/wordlists/*.txt"
          )
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingWords(false)
        }
      }
    }

    loadWords()

    return () => {
      isCancelled = true
    }
  }, [])

  useEffect(() => {
    return () => {
      clearResolveTimer()
      clearConfettiTimer()
    }
  }, [])

  const answersByLength = useMemo(
    () => filterWordsByLength(answerWords, settings.wordLength),
    [answerWords, settings.wordLength]
  )

  const allowedByLength = useMemo(() => {
    const uniqueWords = new Set<string>()

    for (const word of allowedWords) {
      uniqueWords.add(word)
    }

    for (const word of answerWords) {
      uniqueWords.add(word)
    }

    return filterWordsByLength(Array.from(uniqueWords), settings.wordLength)
  }, [allowedWords, answerWords, settings.wordLength])

  const allowedExact = useMemo(
    () =>
      new Set(allowedByLength.map((word) => toNFC(word).toLocaleLowerCase("el"))),
    [allowedByLength]
  )

  const allowedFolded = useMemo(
    () => new Set(allowedByLength.map((word) => foldGreekForCompare(word))),
    [allowedByLength]
  )

  useEffect(() => {
    if (answersByLength.length === 0) {
      setCorrectWord("")
      return
    }

    const weightedWord = pickDailyWeightedWord(
      answerWeights,
      settings.wordLength,
      new Date()
    )

    if (weightedWord) {
      setCorrectWord(weightedWord)
      return
    }

    // Fallback to an unweighted pool if weighted metadata is unavailable.
    const randomIndex = Math.floor(Math.random() * answersByLength.length)
    setCorrectWord(answersByLength[randomIndex] || "")
  }, [answerWeights, answersByLength, settings.wordLength])

  const resetRound = useCallback(() => {
    clearResolveTimer()
    clearConfettiTimer()
    setBoard(createEmptyBoard(settings.wordLength))
    setBoardStatus(createEmptyBoardStatus(settings.wordLength))
    setCurrAttempt({ attempt: 0, letterPos: 0 })
    setLetterStatus(new Map())
    setGameOver({ gameOver: false, guessedWord: false })
    setInvalidMessage("")
    setShakeState({ row: -1, token: 0 })
    setIsResolvingAttempt(false)
    setShowConfetti(false)
  }, [settings.wordLength])

  useEffect(() => {
    resetRound()
  }, [settings.wordLength, settings.diacriticsStrictMode, resetRound])

  const saveSettings = (nextSettings: GameSettings) => {
    setSettings(nextSettings)
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings))
  }

  const onSelectLetter = (key: string) => {
    if (gameOver.gameOver || !correctWord || isResolvingAttempt) {
      return
    }

    if (currAttempt.letterPos >= settings.wordLength) {
      return
    }

    const normalizedKey = toNFC(key)
    const newBoard = [...board]
    newBoard[currAttempt.attempt][currAttempt.letterPos] = normalizedKey
    setBoard(newBoard)
    setCurrAttempt((previous) => ({
      ...previous,
      letterPos: previous.letterPos + 1,
    }))
    setInvalidMessage("")
  }

  const onDelete = () => {
    if (gameOver.gameOver || isResolvingAttempt) {
      return
    }

    if (currAttempt.letterPos === 0) {
      return
    }

    const newBoard = [...board]
    newBoard[currAttempt.attempt][currAttempt.letterPos - 1] = ""
    setBoard(newBoard)
    setCurrAttempt((previous) => ({
      ...previous,
      letterPos: previous.letterPos - 1,
    }))
  }

  const onEnter = () => {
    if (gameOver.gameOver || !correctWord || isResolvingAttempt) {
      return
    }

    if (currAttempt.letterPos !== settings.wordLength) {
      return
    }

    const guess = toNFC(board[currAttempt.attempt].join(""))
    const guessExact = guess.toLocaleLowerCase("el")
    const guessFolded = foldGreekForCompare(guess)

    const wordIsAllowed = settings.diacriticsStrictMode
      ? allowedExact.has(guessExact)
      : allowedFolded.has(guessFolded)

    if (!wordIsAllowed) {
      setInvalidMessage("Word not in list")
      setShakeState((previous) => ({
        row: currAttempt.attempt,
        token: previous.token + 1,
      }))
      return
    }

    const guessStatus = evaluateGuess(
      guess,
      correctWord,
      settings.diacriticsStrictMode
    )

    setBoardStatus((previousStatus) => {
      const nextStatus = [...previousStatus]
      nextStatus[currAttempt.attempt] = guessStatus
      return nextStatus
    })

    setLetterStatus((previousStatuses) =>
      mergeLetterStatus(previousStatuses, guess, guessStatus)
    )

    const nextAttemptCount = currAttempt.attempt + 1
    const didGuessWord = isWinningStatus(guessStatus)
    const didUseLastAttempt = nextAttemptCount === MAX_ATTEMPTS
    const revealDurationMs = getRevealDurationMs(settings.wordLength)
    if (didGuessWord) {
      triggerConfetti()
    }
    setIsResolvingAttempt(true)
    clearResolveTimer()
    resolveTimerRef.current = window.setTimeout(() => {
      setCurrAttempt({
        attempt: nextAttemptCount,
        letterPos: 0,
      })

      if (didGuessWord) {
        setGameOver({ gameOver: true, guessedWord: true })
      } else if (didUseLastAttempt) {
        setGameOver({ gameOver: true, guessedWord: false })
      }

      setIsResolvingAttempt(false)
    }, revealDurationMs)
    setInvalidMessage("")
  }

  const selectedModeLabel = settings.diacriticsStrictMode
    ? "Strict diacritics"
    : "Folded diacritics"

  const canPlay = !isLoadingWords && !wordLoadError && !!correctWord

  return (
    <div className={`App ${settings.darkMode ? "theme-dark" : "theme-light"}`}>
      <div className="animated-background" />
      {showConfetti ? (
        <div className="confetti-layer" aria-hidden>
          {confettiPieces.map((piece, index) => {
            const style: CSSProperties = {
              left: `${piece.left}%`,
              backgroundColor: piece.color,
              ["--size" as string]: `${piece.sizePx}px`,
              ["--drift" as string]: `${piece.drift}px`,
              ["--fall-delay" as string]: `${piece.fallDelayMs}ms`,
              ["--fall-duration" as string]: `${piece.fallDurationMs}ms`,
              ["--spin" as string]: `${piece.spinDeg}deg`,
            }
            return <span className="confetti-piece" key={`${index}-${piece.left}`} style={style} />
          })}
        </div>
      ) : null}
      <header className="top-bar">
        <div>
          <h1>Ancient Greek Wordle</h1>
          <p className="subtitle">
            {settings.wordLength}-letter mode • {selectedModeLabel}
          </p>
        </div>
        <button
          type="button"
          className="settings-open-button top-settings-button"
          onClick={() => setIsSettingsOpen(true)}
        >
          Settings
        </button>
      </header>

      <AppContext.Provider
        value={{
          board,
          setBoard,
          boardStatus,
          setBoardStatus,
          currAttempt,
          setCurrAttempt,
          onDelete,
          onEnter,
          onSelectLetter,
          onReset: resetRound,
          correctWord,
          letterStatus,
          gameOver,
          settings,
          invalidMessage,
          shakeState,
        }}
      >
        <main className="game">
          <Board />
          {canPlay && !gameOver.gameOver ? <Keyboard /> : null}
          {gameOver.gameOver ? <GameOver /> : null}
          {isLoadingWords ? (
            <p className="status-message">Loading Greek words...</p>
          ) : null}
          {wordLoadError ? <p className="status-message error">{wordLoadError}</p> : null}
          {!isLoadingWords && !wordLoadError && !correctWord ? (
            <p className="status-message error">
              No {settings.wordLength}-letter answers available yet. Add more in
              data/gcse_vocab_gr.txt.
            </p>
          ) : null}
          {invalidMessage ? <p className="status-message error">{invalidMessage}</p> : null}
        </main>
      </AppContext.Provider>

      <SettingsModal
        isOpen={isSettingsOpen}
        settings={settings}
        correctWord={correctWord}
        onClose={() => setIsSettingsOpen(false)}
        onSave={saveSettings}
      />

      <div className="floating-actions">
        <button
          type="button"
          className="settings-mini-button"
          onClick={() => setIsSettingsOpen(true)}
        >
          Settings
        </button>
        <button
          type="button"
          className="credits-button"
          onClick={() => setIsCreditsOpen(true)}
        >
          Credits
        </button>
      </div>

      {isCreditsOpen ? (
        <div className="credits-backdrop" onClick={() => setIsCreditsOpen(false)}>
          <div
            className="credits-modal"
            onClick={(event: MouseEvent<HTMLDivElement>) =>
              event.stopPropagation()
            }
          >
            <p>By Henry Hawke and Sean Billington</p>
            <p>thank you natalie for suggesting darkmode</p>
            <p>Fork of jlumbroso's repo</p>
            <button
              type="button"
              className="settings-secondary-button"
              onClick={() => setIsCreditsOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default App
